import React, { useState, useEffect } from 'react'
import {
  LayoutDashboard,
  BarChart2,
  Network,
  FolderKanban,
  Tags,
  Box,
  MessageSquare,
  Users,
  FlaskConical,
  Activity,
  FileText,
  Search,
  Settings,
  ChevronRight,
  ChevronDown,
  Hexagon,
  Palette,
  Check,
  LogOut,
  Loader2,
  Layers,
  Cog,
  Mail,
  Video,
  CalendarDays,
  Clock,
  Phone,
  AlertTriangle,
  Newspaper,
} from 'lucide-react'
import { useTheme } from '@/lib/theme'
import { useAuth } from '@/lib/auth/context'
import { ViewContext, TabId, Organization } from '@/lib/orgs/types'
import { getPlaneClient } from '@/lib/plane/client'
import { PlaneProject } from '@/lib/plane/types'
import { getProjectColor } from '@/lib/plane/colors'
import { SettingsModal } from './settings'

interface SidebarProps {
  currentOrg: ViewContext
  currentTab: TabId
  onOrgChange: (id: ViewContext) => void
  onTabChange: (id: TabId) => void
  organizations?: Organization[]
  isLoading?: boolean
  selectedProjectId?: string | 'all'
  onProjectChange?: (projectId: string | 'all') => void
  isInMeeting?: boolean
  meetingRoomId?: string | null
}

const MENU_ITEMS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'analytics', label: 'Analytics', icon: BarChart2 },
  { id: 'user-graph', label: 'User Graph', icon: Network },
  { id: 'brands', label: 'Brands', icon: Tags },
  { id: 'products', label: 'Products', icon: Box },
  { id: 'reviews', label: 'Reviews', icon: MessageSquare },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'beta-feedback', label: 'Beta Feedback', icon: FlaskConical },
  { id: 'activity', label: 'Activity', icon: Activity },
  { id: 'content', label: 'Content', icon: FileText },
  { id: 'logo-search', label: 'Logo Search', icon: Search },
  { id: 'settings', label: 'Settings', icon: Settings },
]

// Buy'r-specific menu items (inserted after User Graph)
const BUYR_MENU_ITEMS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'recalls', label: 'Recalls', icon: AlertTriangle },
  { id: 'blog', label: 'Blog', icon: Newspaper },
]

// Webb-specific menu (trimmed — no brands/products/reviews/etc.)
const WEBB_MENU_ITEMS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'settings', label: 'Settings', icon: Settings },
]


export function Sidebar({
  currentOrg,
  currentTab,
  onOrgChange,
  onTabChange,
  organizations = [],
  isLoading = false,
  selectedProjectId = 'all',
  onProjectChange,
  isInMeeting = false,
  meetingRoomId = null,
}: SidebarProps) {
  const { currentTheme, setTheme, themes } = useTheme()
  const { adminUser, signOut, isLoading: authLoading } = useAuth()
  const [showThemePicker, setShowThemePicker] = useState(false)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [projectsExpanded, setProjectsExpanded] = useState(false)
  const [calendarExpanded, setCalendarExpanded] = useState(false)
  const [meetingsExpanded, setMeetingsExpanded] = useState(false)
  const [planeProjects, setPlaneProjects] = useState<PlaneProject[]>([])
  const [isLoadingProjects, setIsLoadingProjects] = useState(false)

  // Fetch Plane projects
  useEffect(() => {
    const fetchProjects = async () => {
      const client = getPlaneClient()
      if (!client.isConfigured()) return

      setIsLoadingProjects(true)
      const { data } = await client.getProjects()
      if (data) {
        const projects = Array.isArray(data) ? data : (data as any).results || []
        setPlaneProjects(projects)
      }
      setIsLoadingProjects(false)
    }

    fetchProjects()
  }, [])

  const handleSignOut = async () => {
    setIsSigningOut(true)
    try {
      await signOut()
    } finally {
      setIsSigningOut(false)
    }
  }

  const getUserInitials = () => {
    if (adminUser?.firstName && adminUser?.lastName) {
      return `${adminUser.firstName[0]}${adminUser.lastName[0]}`.toUpperCase()
    }
    if (adminUser?.email) {
      return adminUser.email[0].toUpperCase()
    }
    return '?'
  }

  const getUserDisplayName = () => {
    if (adminUser?.firstName && adminUser?.lastName) {
      return `${adminUser.firstName} ${adminUser.lastName}`
    }
    return adminUser?.email || 'Admin'
  }

  const getRoleLabel = () => {
    switch (adminUser?.role) {
      case 'super_admin':
        return 'Super Admin'
      case 'content_manager':
        return 'Content Manager'
      case 'support':
        return 'Support'
      default:
        return 'User'
    }
  }

  return (
    <div className="w-64 h-screen bg-[var(--bg-card)] border-r border-[var(--border)] flex flex-col fixed left-0 top-0 z-50 transition-colors duration-300 overflow-hidden">
      {/* Brand Header - Click to go to Global View */}
      <div
        className="h-16 flex items-center px-6 border-b border-[var(--border)] cursor-pointer hover:bg-[var(--bg-hover)] transition-colors shrink-0"
        onClick={() => {
          setProjectsExpanded(false)
          onOrgChange('global') // This already sets tab to 'overview'
        }}
      >
        <Hexagon className="w-6 h-6 text-[var(--primary)] fill-[var(--primary)]/20 mr-3" />
        <span className="text-lg font-bold text-[var(--text-main)] tracking-tight">
          Mentatix
        </span>
      </div>

      {/* Organization Switcher */}
      <div className="px-4 pt-4 pb-2 shrink-0">
        <h3 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-3 px-2">
          Organizations
        </h3>

        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-[var(--text-muted)]" />
          </div>
        ) : (
          <div className="space-y-1">
            {/* Global Option */}
            <button
              type="button"
              onClick={() => {
                setProjectsExpanded(false)
                onOrgChange('global') // This already sets tab to 'overview'
              }}
              className={`w-full flex items-center px-2 py-2 rounded-lg text-sm font-medium transition-all duration-200 group ${
                currentOrg === 'global' && currentTab !== 'projects'
                  ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              <div className={`w-8 h-8 rounded-md flex items-center justify-center mr-3 transition-colors ${
                currentOrg === 'global' && currentTab !== 'projects'
                  ? 'bg-[var(--primary)] text-[var(--bg-main)]'
                  : 'bg-[var(--bg-element)] text-[var(--text-muted)] group-hover:bg-[var(--bg-hover)]'
              }`}>
                <LayoutDashboard size={16} />
              </div>
              <span>Global View</span>
              {currentOrg === 'global' && currentTab !== 'projects' && <ChevronRight size={14} className="ml-auto" />}
            </button>

            {/* Projects - Global PM (Always Visible) */}
            <div>
              <button
                onClick={() => {
                  if (projectsExpanded) {
                    setProjectsExpanded(false)
                  } else {
                    setProjectsExpanded(true)
                    setCalendarExpanded(false)
                    // Switch to global view if not already there
                    if (currentOrg !== 'global') {
                      onOrgChange('global')
                    }
                    onTabChange('projects')
                  }
                }}
                className={`w-full flex items-center px-2 py-2 rounded-lg text-sm font-medium transition-all duration-200 group ${
                  currentTab === 'projects'
                    ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                <div className={`w-8 h-8 rounded-md flex items-center justify-center mr-3 transition-colors ${
                  currentTab === 'projects'
                    ? 'bg-[var(--primary)] text-[var(--bg-main)]'
                    : 'bg-[var(--bg-element)] text-[var(--text-muted)] group-hover:bg-[var(--bg-hover)]'
                }`}>
                  <FolderKanban size={16} />
                </div>
                <span>Projects</span>
                {projectsExpanded ? (
                  <ChevronDown size={14} className="ml-auto" />
                ) : (
                  <ChevronRight size={14} className="ml-auto" />
                )}
              </button>

              {/* Projects Sub-menu */}
              {projectsExpanded && (
                <div className="ml-5 mt-1 space-y-0.5 border-l border-[var(--border)] pl-3">
                  {/* Overview - All Projects */}
                  <button
                    onClick={() => {
                      if (currentOrg !== 'global') {
                        onOrgChange('global')
                      }
                      onTabChange('projects')
                      onProjectChange?.('all')
                    }}
                    className={`w-full flex items-center px-2 py-1.5 rounded-md text-sm transition-colors ${
                      currentTab === 'projects' && selectedProjectId === 'all'
                        ? 'bg-[var(--bg-element)] text-[var(--text-main)]'
                        : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)]'
                    }`}
                  >
                    <Layers size={14} className="mr-2" />
                    <span>All Tasks</span>
                    {currentTab === 'projects' && selectedProjectId === 'all' && (
                      <span className="ml-auto text-xs text-[var(--text-muted)]">
                        {planeProjects.reduce((sum, p) => sum + ((p as any).total_issues || 0), 0)}
                      </span>
                    )}
                  </button>

                  {/* Individual Projects */}
                  {isLoadingProjects ? (
                    <div className="flex items-center justify-center py-2">
                      <Loader2 size={14} className="animate-spin text-[var(--text-muted)]" />
                    </div>
                  ) : (
                    planeProjects.map((project) => (
                      <button
                        key={project.id}
                        onClick={() => {
                          if (currentOrg !== 'global') {
                            onOrgChange('global')
                          }
                          onTabChange('projects')
                          onProjectChange?.(project.id)
                        }}
                        className={`w-full flex items-center px-2 py-1.5 rounded-md text-sm transition-colors ${
                          currentTab === 'projects' && selectedProjectId === project.id
                            ? 'bg-[var(--bg-element)] text-[var(--text-main)]'
                            : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)]'
                        }`}
                      >
                        <div
                          className="w-3 h-3 rounded mr-2"
                          style={{ backgroundColor: getProjectColor(project.name) }}
                        />
                        <span className="truncate flex-1 text-left">{project.name}</span>
                        {(project as any).total_issues !== undefined && (
                          <span className="ml-auto text-xs text-[var(--text-muted)]">
                            {(project as any).total_issues}
                          </span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Webmail - Global View (Always Visible) */}
            <button
              onClick={() => {
                setProjectsExpanded(false)
                setCalendarExpanded(false)
                if (currentOrg !== 'global') {
                  onOrgChange('global')
                }
                onTabChange('webmail')
              }}
              className={`w-full flex items-center px-2 py-2 rounded-lg text-sm font-medium transition-all duration-200 group ${
                currentTab === 'webmail'
                  ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              <div className={`w-8 h-8 rounded-md flex items-center justify-center mr-3 transition-colors ${
                currentTab === 'webmail'
                  ? 'bg-[var(--primary)] text-[var(--bg-main)]'
                  : 'bg-[var(--bg-element)] text-[var(--text-muted)] group-hover:bg-[var(--bg-hover)]'
              }`}>
                <Mail size={16} />
              </div>
              <span>Webmail</span>
              {currentTab === 'webmail' && <ChevronRight size={14} className="ml-auto" />}
            </button>

            {/* Meeting Rooms - Global View (Always Visible) */}
            <div>
              <button
                onClick={() => {
                  if (isInMeeting && meetingsExpanded) {
                    setMeetingsExpanded(false)
                  } else if (isInMeeting) {
                    setProjectsExpanded(false)
                    setCalendarExpanded(false)
                    setMeetingsExpanded(true)
                    if (currentOrg !== 'global') {
                      onOrgChange('global')
                    }
                    onTabChange('meetings')
                  } else {
                    setProjectsExpanded(false)
                    setCalendarExpanded(false)
                    setMeetingsExpanded(false)
                    if (currentOrg !== 'global') {
                      onOrgChange('global')
                    }
                    onTabChange('meetings')
                  }
                }}
                className={`w-full flex items-center px-2 py-2 rounded-lg text-sm font-medium transition-all duration-200 group ${
                  currentTab === 'meetings'
                    ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                <div className={`relative w-8 h-8 rounded-md flex items-center justify-center mr-3 transition-colors ${
                  currentTab === 'meetings'
                    ? 'bg-[var(--primary)] text-[var(--bg-main)]'
                    : 'bg-[var(--bg-element)] text-[var(--text-muted)] group-hover:bg-[var(--bg-hover)]'
                }`}>
                  <Video size={16} />
                  {/* Red pulsing indicator when in meeting */}
                  {isInMeeting && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse shadow-lg shadow-red-500/50" />
                  )}
                </div>
                <span>Meeting Rooms</span>
                {isInMeeting ? (
                  meetingsExpanded ? (
                    <ChevronDown size={14} className="ml-auto" />
                  ) : (
                    <ChevronRight size={14} className="ml-auto" />
                  )
                ) : (
                  currentTab === 'meetings' && <ChevronRight size={14} className="ml-auto" />
                )}
              </button>

              {/* Active meeting child tab */}
              {isInMeeting && meetingsExpanded && (
                <div className="ml-5 mt-1 space-y-0.5 border-l border-[var(--border)] pl-3">
                  <button
                    onClick={() => {
                      if (currentOrg !== 'global') {
                        onOrgChange('global')
                      }
                      onTabChange('meetings')
                    }}
                    className={`w-full flex items-center px-2 py-1.5 rounded-md text-sm transition-colors ${
                      currentTab === 'meetings'
                        ? 'bg-[var(--bg-element)] text-[var(--text-main)]'
                        : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)]'
                    }`}
                  >
                    <Phone size={14} className="mr-2 text-green-400" />
                    <span className="truncate">Room: {meetingRoomId?.slice(0, 8) || 'Active'}</span>
                  </button>
                </div>
              )}
            </div>

            {/* Calendar - Expandable Section */}
            <div>
              <button
                onClick={() => {
                  if (calendarExpanded) {
                    setCalendarExpanded(false)
                  } else {
                    setProjectsExpanded(false)
                    setCalendarExpanded(true)
                    if (currentOrg !== 'global') {
                      onOrgChange('global')
                    }
                    onTabChange('calendar')
                  }
                }}
                className={`w-full flex items-center px-2 py-2 rounded-lg text-sm font-medium transition-all duration-200 group ${
                  currentTab === 'calendar' || currentTab === 'availability'
                    ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                <div className={`w-8 h-8 rounded-md flex items-center justify-center mr-3 transition-colors ${
                  currentTab === 'calendar' || currentTab === 'availability'
                    ? 'bg-[var(--primary)] text-[var(--bg-main)]'
                    : 'bg-[var(--bg-element)] text-[var(--text-muted)] group-hover:bg-[var(--bg-hover)]'
                }`}>
                  <CalendarDays size={16} />
                </div>
                <span>Calendar</span>
                {calendarExpanded ? (
                  <ChevronDown size={14} className="ml-auto" />
                ) : (
                  <ChevronRight size={14} className="ml-auto" />
                )}
              </button>

              {/* Calendar Sub-menu */}
              {calendarExpanded && (
                <div className="ml-5 mt-1 space-y-0.5 border-l border-[var(--border)] pl-3">
                  {/* My Calendar */}
                  <button
                    onClick={() => {
                      if (currentOrg !== 'global') {
                        onOrgChange('global')
                      }
                      onTabChange('calendar')
                    }}
                    className={`w-full flex items-center px-2 py-1.5 rounded-md text-sm transition-colors ${
                      currentTab === 'calendar'
                        ? 'bg-[var(--bg-element)] text-[var(--text-main)]'
                        : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)]'
                    }`}
                  >
                    <CalendarDays size={14} className="mr-2" />
                    <span>My Calendar</span>
                  </button>

                  {/* Public Availability */}
                  <button
                    onClick={() => {
                      if (currentOrg !== 'global') {
                        onOrgChange('global')
                      }
                      onTabChange('availability')
                    }}
                    className={`w-full flex items-center px-2 py-1.5 rounded-md text-sm transition-colors ${
                      currentTab === 'availability'
                        ? 'bg-[var(--bg-element)] text-[var(--text-main)]'
                        : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)]'
                    }`}
                  >
                    <Clock size={14} className="mr-2" />
                    <span>Public Availability</span>
                  </button>
                </div>
              )}
            </div>

            {/* Org List */}
            {organizations.map((org) => {
              const isSelected = currentOrg === org.slug
              const isDisabled = !org.isConfigured || !org.isActive
              const OrgIcon = org.icon

              return (
                <button
                  key={org.slug}
                  onClick={() => !isDisabled && onOrgChange(org.slug)}
                  disabled={isDisabled}
                  className={`w-full flex items-center px-2 py-2 rounded-lg text-sm font-medium transition-all duration-200 group ${
                    isDisabled
                      ? 'opacity-50 cursor-not-allowed'
                      : isSelected
                        ? 'bg-[var(--bg-element)] text-[var(--text-main)]'
                        : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-element)]'
                  }`}
                  title={isDisabled ? 'Coming Soon' : org.description}
                >
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center mr-3 transition-colors ${
                    isSelected
                      ? 'bg-[var(--bg-hover)]'
                      : 'bg-[var(--bg-element)] group-hover:bg-[var(--bg-hover)]'
                  }`}>
                    <OrgIcon size={16} className={org.color} />
                  </div>
                  <span className="flex-1 text-left">{org.name}</span>
                  {isDisabled ? (
                    <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] bg-[var(--bg-element)] px-1.5 py-0.5 rounded">
                      Soon
                    </span>
                  ) : isSelected ? (
                    <ChevronRight size={14} className="text-[var(--text-muted)]" />
                  ) : null}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Context Menu - Organization View */}
      {currentOrg !== 'global' && (() => {
        // Combine menu items: for Buy'r, insert BUYR_MENU_ITEMS after User Graph (index 2)
        const menuItems = currentOrg === 'buyr'
          ? [...MENU_ITEMS.slice(0, 3), ...BUYR_MENU_ITEMS, ...MENU_ITEMS.slice(3)]
          : currentOrg === 'webb'
            ? WEBB_MENU_ITEMS
            : MENU_ITEMS

        return (
          <div className="flex-1 min-h-0 overflow-y-auto py-2 px-4 space-y-1 custom-scrollbar">
            <h3 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-2 px-2 mt-2">
              {organizations.find(o => o.slug === currentOrg)?.name || currentOrg} Menu
            </h3>
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => onTabChange(item.id)}
                className={`w-full flex items-center px-3 py-2 rounded-md text-sm transition-colors ${
                  currentTab === item.id
                    ? 'bg-[var(--bg-element)] text-[var(--text-main)] border-l-2 border-[var(--primary)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)] border-l-2 border-transparent'
                }`}
              >
                <item.icon
                  size={16}
                  className={`mr-3 ${currentTab === item.id ? 'text-[var(--primary)]' : 'text-[var(--text-muted)]'}`}
                />
                {item.label}
              </button>
            ))}
          </div>
        )
      })()}

      {/* Spacer when in global view */}
      {currentOrg === 'global' && <div className="flex-1" />}

      {/* Theme Picker Popup */}
      {showThemePicker && (
        <div className="absolute bottom-20 left-4 right-4 bg-[var(--bg-element)] border border-[var(--border)] rounded-xl p-3 shadow-2xl z-50 animate-fade-in">
          <h4 className="text-xs font-bold text-[var(--text-main)] mb-3 uppercase tracking-wider">
            Select Theme
          </h4>
          <div className="grid grid-cols-5 gap-2">
            {themes.map(theme => (
              <button
                key={theme.id}
                onClick={() => { setTheme(theme.id); setShowThemePicker(false) }}
                className="w-8 h-8 rounded-full border-2 border-[var(--border)] hover:scale-110 transition-transform flex items-center justify-center relative group"
                style={{ backgroundColor: theme.colors.primary }}
                title={theme.name}
              >
                {currentTheme.id === theme.id && (
                  <Check size={14} className="text-white drop-shadow-md" />
                )}
                <span className="absolute -bottom-8 bg-black text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-50 pointer-events-none">
                  {theme.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Footer User Profile */}
      <div className="p-4 border-t border-[var(--border)] mt-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center min-w-0 flex-1">
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-[var(--primary)] to-purple-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {getUserInitials()}
            </div>
            <div className="ml-3 min-w-0">
              <p className="text-sm font-medium text-[var(--text-main)] truncate">
                {getUserDisplayName()}
              </p>
              <p className="text-xs text-[var(--text-muted)] truncate">
                {getRoleLabel()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => setShowThemePicker(!showThemePicker)}
              className={`p-2 rounded-lg hover:bg-[var(--bg-hover)] text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors ${
                showThemePicker ? 'bg-[var(--bg-hover)] text-[var(--primary)]' : ''
              }`}
              title="Change Theme"
            >
              <Palette size={18} />
            </button>
            <button
              onClick={() => setShowSettingsModal(true)}
              className="p-2 rounded-lg hover:bg-[var(--bg-hover)] text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
              title="Settings"
            >
              <Cog size={18} />
            </button>
            <button
              onClick={handleSignOut}
              disabled={isSigningOut}
              className="p-2 rounded-lg hover:bg-[var(--bg-hover)] text-[var(--text-muted)] hover:text-red-400 transition-colors disabled:opacity-50"
              title="Sign Out"
            >
              {isSigningOut ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <LogOut size={18} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />
    </div>
  )
}
